export const REPORT_CATEGORIES = [
  { id: 'bug', label: 'Bug / issue' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'feature', label: 'Feature request' }
]

const REPORT_TABS = new Set(['questions', 'helper', 'view-work'])
const MAX_JPEG_CHARS = 1200000

export const createReportId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16)
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

const asText = (value) => (typeof value === 'string' ? value : '')

const questionTextOf = (source) => {
  if (!source) return ''
  if (typeof source.question === 'string') {
    return source.question
  }
  return asText(source.question?.latex)
    || asText(source.question?.text)
    || asText(source.questionLatex)
    || asText(source.questionText)
}

const lastAttemptOf = (item) => {
  const attempts = Array.isArray(item?.attempts) && item.attempts.length > 0
    ? item.attempts
    : Array.isArray(item?.allAttempts) && item.allAttempts.length > 0
      ? item.allAttempts
      : [{
          answer: item?.answer,
          feedback: item?.feedback,
          studentAnswer: item?.studentAnswer
        }]
  return attempts[attempts.length - 1] || null
}

const contextFromViewWork = (item) => {
  if (item.workItemType === 'explain') {
    const stepCount = Array.isArray(item.steps) ? item.steps.length : 0
    const rawIndex = Number(item.currentStepIndex)
    const stepIndex = Number.isInteger(rawIndex) && rawIndex >= 0
      ? Math.min(rawIndex, Math.max(stepCount - 1, 0))
      : 0
    const step = stepCount > 0 ? item.steps[stepIndex] : null
    return {
      kind: 'explain',
      questionId: null,
      sessionId: asText(item.id) || null,
      questionText: questionTextOf(item),
      answerText: asText(item.finalAnswer),
      feedbackText: '',
      topic: asText(item.topic) || null,
      stepIndex,
      stepCount: stepCount || null,
      stepText: asText(step?.explanation) || asText(step?.solution)
    }
  }

  const attempt = lastAttemptOf(item)
  return {
    kind: 'check-answer',
    questionId: asText(item.id) || null,
    sessionId: null,
    questionText: questionTextOf(item),
    answerText: asText(attempt?.answer?.latex)
      || asText(attempt?.answer?.text)
      || asText(attempt?.studentAnswer),
    feedbackText: asText(attempt?.feedback?.message),
    topic: asText(item.topic) || null,
    stepIndex: null,
    stepCount: null,
    stepText: ''
  }
}

/**
 * The exercise on screen, or the one still open in Study Helper if the student
 * has switched tabs. View Work wins only while that tab is the one they are
 * looking at. Ids stay in the payload so the admin email can name the record;
 * the parent email drops them on the server.
 */
export const buildExerciseContext = ({
  selectedMenuItem,
  explainSession,
  explainStepIndex,
  question,
  answer,
  feedback,
  questionMetadata,
  selectedAssignment,
  studyQuestionId,
  selectedViewWorkQuestion
}) => {
  if (selectedMenuItem === 'view-work' && selectedViewWorkQuestion) {
    const viewed = contextFromViewWork(selectedViewWorkQuestion)
    return viewed.questionText ? viewed : null
  }

  if (explainSession) {
    const steps = Array.isArray(explainSession.steps) ? explainSession.steps : []
    const rawIndex = Number(explainStepIndex)
    const stepIndex = Number.isInteger(rawIndex) && rawIndex >= 0
      ? Math.min(rawIndex, Math.max(steps.length - 1, 0))
      : 0
    const step = steps[stepIndex]
    const questionText = questionTextOf(explainSession)
    if (!questionText) return null
    const onLastStep = steps.length > 0 && stepIndex >= steps.length - 1
    return {
      kind: 'explain',
      questionId: null,
      sessionId: asText(explainSession.sessionId) || null,
      questionText,
      answerText: onLastStep ? asText(explainSession.finalAnswer) : '',
      feedbackText: '',
      topic: asText(explainSession.topic) || null,
      stepIndex,
      stepCount: steps.length || null,
      stepText: asText(step?.explanation) || asText(step?.solution)
    }
  }

  const questionText = asText(question?.latex) || asText(question?.text)
  if (!questionText) return null
  return {
    kind: 'check-answer',
    questionId: asText(selectedAssignment?.id) || asText(studyQuestionId) || null,
    sessionId: null,
    questionText,
    answerText: asText(answer?.latex) || asText(answer?.text),
    feedbackText: asText(feedback?.message),
    topic: asText(questionMetadata?.topic) || null,
    stepIndex: null,
    stepCount: null,
    stepText: ''
  }
}

export const buildPageInfo = (selectedMenuItem) => {
  const tab = REPORT_TABS.has(selectedMenuItem) ? selectedMenuItem : 'helper'
  const origin = typeof window !== 'undefined' && window.location
    ? window.location.origin
    : ''
  const pathname = typeof window !== 'undefined' && window.location
    ? window.location.pathname
    : ''
  return {
    url: `${origin}${pathname}`.slice(0, 2000),
    tab,
    userAgent: typeof navigator !== 'undefined'
      ? String(navigator.userAgent || '').slice(0, 300)
      : '',
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0
  }
}

const nextFrame = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve))
})

export const maskStudentNames = () => {
  const nodes = Array.from(document.querySelectorAll('[data-report-mask="student-name"]'))
  const saved = nodes.map((node) => ({ node, text: node.textContent }))
  nodes.forEach((node) => {
    const length = Math.min(Math.max((node.textContent || '').trim().length, 4), 24)
    node.textContent = '•'.repeat(length)
  })
  return () => {
    saved.forEach(({ node, text }) => {
      node.textContent = text
    })
  }
}

const loadHtml2Canvas = async () => {
  const module = await import('html2canvas')
  return module.default
}

const canvasToJpeg = (canvas) => {
  let quality = 0.72
  let dataUrl = canvas.toDataURL('image/jpeg', quality)
  while (dataUrl.length > MAX_JPEG_CHARS && quality > 0.42) {
    quality = Math.round((quality - 0.1) * 100) / 100
    dataUrl = canvas.toDataURL('image/jpeg', quality)
  }
  if (dataUrl.length <= MAX_JPEG_CHARS) return dataUrl

  const scale = Math.sqrt(MAX_JPEG_CHARS / dataUrl.length) * 0.92
  const smaller = document.createElement('canvas')
  smaller.width = Math.max(1, Math.floor(canvas.width * scale))
  smaller.height = Math.max(1, Math.floor(canvas.height * scale))
  const context = smaller.getContext('2d')
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, smaller.width, smaller.height)
  context.drawImage(canvas, 0, 0, smaller.width, smaller.height)
  return smaller.toDataURL('image/jpeg', 0.7)
}

const ignoredInPageCapture = (element) => {
  if (!element || !element.classList) return false
  return element.classList.contains('student-report-modal')
    || element.classList.contains('student-report-exercise-sheet')
}

export const captureViewportJpeg = async () => {
  const html2canvas = await loadHtml2Canvas()
  const canvas = await html2canvas(document.body, {
    x: window.scrollX,
    y: window.scrollY,
    width: window.innerWidth,
    height: window.innerHeight,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scrollX: 0,
    scrollY: 0,
    useCORS: true,
    allowTaint: false,
    logging: false,
    scale: 1,
    backgroundColor: '#ffffff',
    ignoreElements: ignoredInPageCapture
  })
  return canvasToJpeg(canvas)
}

export const waitForRenderedMath = async (element) => {
  const source = element?.textContent || ''
  if (!/[$\\]/.test(source) && !element?.querySelector?.('.katex, mjx-container, .mjx-container')) {
    return
  }
  const started = Date.now()
  while (Date.now() - started < 1800) {
    if (element.querySelector('.katex, mjx-container, .mjx-container')) return
    await new Promise((resolve) => setTimeout(resolve, 50))
  }
}

export const captureExerciseJpeg = async () => {
  const marked = Array.from(document.querySelectorAll('[data-report-exercise]'))
  const live = marked.find((node) => !node.closest('.student-report-exercise-sheet'))
  const fallback = document.querySelector('[data-report-exercise-fallback]')
    || marked.find((node) => node.closest('.student-report-exercise-sheet'))
  const target = live || fallback
  if (!target) return null

  const sheet = target.closest('.student-report-exercise-sheet')
  const parkedLeft = sheet ? sheet.style.left : ''
  if (sheet) {
    sheet.style.left = '0px'
    sheet.style.zIndex = '-1'
  }

  await nextFrame()
  await waitForRenderedMath(target)
  const html2canvas = await loadHtml2Canvas()
  const height = Math.max(target.scrollHeight, target.getBoundingClientRect().height)
  const scale = height > 2200 ? 0.6 : 1
  try {
    const canvas = await html2canvas(target, {
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale,
      windowWidth: Math.max(target.scrollWidth, 720),
      windowHeight: Math.max(target.scrollHeight, 200)
    })
    return canvasToJpeg(canvas)
  } finally {
    if (sheet) {
      sheet.style.left = parkedLeft
      sheet.style.zIndex = ''
    }
  }
}

export const preparePageForCapture = async () => {
  await nextFrame()
  const restoreNames = maskStudentNames()
  await nextFrame()
  return restoreNames
}

export const submitStudentReport = async (payload) => {
  const { httpsCallable } = await import('firebase/functions')
  const { functions } = await import('./firebase')
  const submit = httpsCallable(functions, 'submitStudentReport', { timeout: 60000 })
  const result = await submit(payload)
  return result.data
}
